<script lang="ts">
  import Bars from '../charts/Bars.svelte';
  import Chart from '../charts/Chart.svelte';
  import Readout, { type Item } from '../charts/Readout.svelte';
  import { decimals, niceTicks, polyline } from '../../utils/chart';
  import { t } from '../../i18n/index.ts';

  // BarChart is a bar per hour, day, week, or month on a Health card
  // (spec 007 FR-019, 007 FR-020) in the shared chart language (spec 010
  // 010 FR-033): a missing value is a gap, not a zero bar (007 FR-022). inner
  // stacks a part of each bar in its own color, such as deep sleep within
  // time asleep. typical draws the Typical as a line across, and
  // typicalSeries as a dashed line over a faint area behind the bars
  // (007 FR-021). Hovering or tapping a bar reads it above the chart, and the
  // sr-only table gives a screen reader every value.
  interface Props {
    values: (number | null | undefined)[];
    inner?: (number | null | undefined)[];
    color: string;
    innerColor?: string;
    typical?: number;
    typicalSeries?: number[];
    // labels go under each bar; ticks instead place a few labels by position, 0 to 1.
    labels?: string[];
    ticks?: { at: number; text: string }[];
    names: string[];
    caption: string;
    format: (v: number) => string;
    heading: { name: string; value: string; inner?: string; typical?: string };
    // unit heads the value scale; steps and tick make its values round
    // in the measure's own terms, such as whole hours of sleep.
    unit?: string;
    steps?: number[];
    tick?: (v: number) => string;
  }
  let { values, inner, color, innerColor, typical, typicalSeries, labels, ticks, names, caption, format, heading, unit, steps, tick }: Props = $props();

  const TYPICAL_COLOR = 'var(--theme-yellow)';

  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  let shown = $derived(hover ?? at);

  let n = $derived(values.length || 1);
  let positions = $derived(values.map((_, i) => (i + 0.5) / n));
  let scale = $derived(niceTicks(0, Math.max(0, ...values.map((v) => v ?? 0), typical ?? 0, ...(typicalSeries ?? [])), { zero: true, steps, whole: !steps }));
  let typicalLine = $derived(typicalSeries?.length ? polyline(typicalSeries.map((v, i) => [(i + 0.5) / typicalSeries.length, v] as [number, number]), scale.lo, scale.hi) : '');
  let xTicks = $derived(labels ? labels.map((l, i) => ({ at: positions[i], text: l })) : (ticks ?? []));

  let items = $derived.by((): Item[] => {
    const val = (v: number | null | undefined) => (shown == null ? undefined : v != null ? format(v) : t('healthNoData'));
    const out: Item[] = [{ label: heading.value, value: val(shown != null ? values[shown] : null), color, mark: 'bar' }];
    if (inner && heading.inner) out.push({ label: heading.inner, value: val(shown != null ? inner[shown] : null), color: innerColor ?? color, mark: 'bar' });
    if (typicalSeries?.length) out.push({ label: heading.typical ?? t('healthTypical'), value: val(shown != null ? typicalSeries[shown] : null), color: TYPICAL_COLOR, mark: 'dash' });
    else if (typical != null && typical > 0) out.push({ label: t('healthTypical'), value: format(typical), color: TYPICAL_COLOR });
    return out;
  });
</script>

<div class="space-y-2">
  {#if items.length === 1}
    <Readout title={shown != null ? t('healthChartValue', { name: names[shown], value: items[0].value }) : t('healthHoverBar')} idle={shown == null} items={[]} />
  {:else}
    <Readout title={shown != null ? names[shown] : t('healthHoverBar')} idle={shown == null} {items} />
  {/if}
  <Chart lo={scale.lo} hi={scale.hi} {unit} yTicks={scale.ticks.map((v) => ({ v, text: tick ? tick(v) : v.toFixed(decimals(scale.step)) }))}
    {xTicks} xGrid={!labels} height="h-32" {positions} bind:at bind:hover label={t('healthChartLabel', { caption })}
    valueText={shown == null ? t('healthNoBarPicked') : values[shown] != null ? t('healthChartValue', { name: names[shown], value: format(values[shown]!) }) : t('healthChartNoValue', { name: names[shown] })}>
    {#snippet plot()}
      {#if typicalLine}
        <polygon points="{(0.5 / n) * 100},100 {typicalLine} {(1 - 0.5 / n) * 100},100" fill={TYPICAL_COLOR} fill-opacity="0.12" />
      {/if}
    {/snippet}
    {#snippet overlay()}
      <Bars bars={values.map((v, i) => ({ at: positions[i], v: v ?? 0, color, opacity: inner ? 0.45 : 0.85 })).filter((b) => b.v > 0)} width={0.7 / n} lo={scale.lo} hi={scale.hi} />
      {#if inner}
        <Bars bars={inner.map((v, i) => ({ at: positions[i], v: v ?? 0, color: innerColor ?? color })).filter((b) => b.v > 0)} width={0.7 / n} lo={scale.lo} hi={scale.hi} />
      {/if}
      <svg viewBox="0 0 100 100" class="absolute inset-0 w-full h-full overflow-visible pointer-events-none" preserveAspectRatio="none" aria-hidden="true">
        {#if typicalLine}
          <polyline points={typicalLine} fill="none" stroke={TYPICAL_COLOR} stroke-width="2" stroke-dasharray="5 4" vector-effect="non-scaling-stroke" />
        {:else if typical != null && typical > 0}
          {@const ty = ((scale.hi - typical) / (scale.hi - scale.lo)) * 100}
          <line x1="0" x2="100" y1={ty} y2={ty} stroke={TYPICAL_COLOR} stroke-width="2" vector-effect="non-scaling-stroke" />
        {/if}
      </svg>
      {#if shown != null}
        <span class="absolute inset-y-0 pointer-events-none rounded-sm" style="left: {(shown / n) * 100}%; width: {100 / n}%; background: color-mix(in srgb, var(--theme-text) 8%, transparent);" aria-hidden="true"></span>
      {/if}
    {/snippet}
  </Chart>
</div>
<table class="sr-only">
  <caption>{caption}</caption>
  <thead>
    <tr><th>{heading.name}</th><th>{heading.value}</th>{#if heading.inner}<th>{heading.inner}</th>{/if}{#if heading.typical}<th>{heading.typical}</th>{/if}</tr>
  </thead>
  <tbody>
    {#each names as name, i (i)}
      <tr>
        <td>{name}</td>
        <td>{values[i] != null ? format(values[i]!) : t('healthNoData')}</td>
        {#if heading.inner}<td>{inner?.[i] != null ? format(inner[i]!) : t('healthNoData')}</td>{/if}
        {#if heading.typical}<td>{typicalSeries?.[i] != null ? format(typicalSeries[i]) : typical != null ? format(typical) : t('healthNoData')}</td>{/if}
      </tr>
    {/each}
  </tbody>
</table>
